import { useCallback, useEffect, useMemo, useRef } from 'react';

import { formatPercentage, isEmpty } from '@ajaibid/utils';
import useTracker from '@hooks/useTracker';
import stockService from '@services/stock';

import useRealtimeStockPrice from '@context/websocket/useRealtimeStockPrice';
import useStockBuySellContext from '../StockBuySell/hooks/useStockBuySellContext';
import { getAdditionalComponents, getAtoDuration, getAtoTriggerPrice } from './helpers';
import {
  CLICK_ORDER_BOOK_PRICE,
  VIEW_TRADE_PANEL,
  CLICK_SWITCH_ORDER_SIDE,
  CLICK_SWITCH_ORDER_TYPE,
  CLICK_SUBMIT_BUY_BUTTON,
  CLICK_BUY_BUTTON_CONFIRM,
  CLICK_SUBMIT_SELL_BUTTON,
  CLICK_SELL_BUTTON_CONFIRM,
  ORDER_SEND_SUCCESS,
  ORDER_SEND_FAIL,
  VIEW_INSUFFICIENT_ERROR,
  CLICK_INSUFFICIENT_CTA,
  CLICK_SEARCH_DISCOVERY,
} from './constants';

const DEFAULT_IS_FIRED = {
  [VIEW_TRADE_PANEL]: false,
};

function useEventTracker() {
  const isFired = useRef(DEFAULT_IS_FIRED);

  const { trackEvent } = useTracker();

  const { code, formState, formKey, isLimitOrder, isAutoOrder, maxBuy, maxSell } =
    useStockBuySellContext();
  const { data: stockAbout } = stockService.detail.about.useAbout(code);
  const { data: stockPrice } = useRealtimeStockPrice(code);
  const { isMarketOpen } = stockService.global.tradingtime.useTradingTime();

  useEffect(() => {
    // Reset isFired on page change
    isFired.current = DEFAULT_IS_FIRED;
  }, [code]);

  const stockProps = useMemo(
    () => ({
      page_name: 'Stock Details Page',
      // funnel_source: 'Stock Ticker Tab',
      stock: code,
      pct_price: formatPercentage(stockPrice?.return_pct) || null,
      additional_component: getAdditionalComponents({ stockAbout, isMarketOpen }),
    }),
    [code, stockAbout, stockPrice, isMarketOpen]
  );

  const formProps = {
    order_side: formState?.[formKey.INPUT_ORDER_SIDE],
    account_type: formState?.[formKey.INPUT_ACCOUNT_TYPE]?.label,
    order_type: formState?.[formKey.TAB_ORDER_TYPE]?.key,
    price: formState?.[formKey.INPUT_PRICE],
    lot: formState?.[formKey.INPUT_LOT_COUNT],
    total: formState?.[formKey.CALC_ORDER_TOTAL],
    buying_power: maxBuy?.label,
    available_lot: maxSell?.label,
    ...(isLimitOrder
      ? {
          period: formState?.[formKey.INPUT_ORDER_PERIOD]?.value,
        }
      : {}),
    ...(isAutoOrder
      ? {
          ato_start: formState[formKey.INPUT_START_DATE],
          ato_end: formState[formKey.INPUT_END_DATE],
          ato_duration: getAtoDuration(formState),
          ato_trigger_price: getAtoTriggerPrice(formState),
        }
      : {}),
  };

  const eventViewTradePanel = useCallback(() => {
    if (isFired.current[VIEW_TRADE_PANEL]) {
      // Only fire view events once
      return;
    }
    if (isEmpty(stockPrice) || isEmpty(stockAbout)) {
      // Only fire when all data is ready
      return;
    }

    trackEvent(VIEW_TRADE_PANEL, { ...stockProps });
    isFired.current[VIEW_TRADE_PANEL] = true;
  }, [stockPrice, stockAbout, stockProps]);

  const eventClickSearchDiscovery = () => {
    trackEvent(CLICK_SEARCH_DISCOVERY, { ...stockProps, ...formProps });
  };

  const eventClickSwitchOrderSide = ({ orderSide }) => {
    trackEvent(CLICK_SWITCH_ORDER_SIDE, { ...stockProps, ...formProps, order_side: orderSide });
  };

  const eventClickSwitchOrderType = ({ type }) => {
    trackEvent(CLICK_SWITCH_ORDER_TYPE, { ...stockProps, ...formProps, order_type: type });
  };

  const eventClickSubmitBuy = () => {
    trackEvent(CLICK_SUBMIT_BUY_BUTTON, { ...stockProps, ...formProps });
  };

  const eventClickConfirmBuy = () => {
    trackEvent(CLICK_BUY_BUTTON_CONFIRM, { ...stockProps, ...formProps });
  };

  const eventClickSubmitSell = () => {
    trackEvent(CLICK_SUBMIT_SELL_BUTTON, { ...stockProps, ...formProps });
  };

  const eventClickConfirmSell = () => {
    trackEvent(CLICK_SELL_BUTTON_CONFIRM, { ...stockProps, ...formProps });
  };

  const eventOrderSendSuccess = () => {
    trackEvent(ORDER_SEND_SUCCESS, { ...stockProps, ...formProps });
  };

  const eventOrderSendFail = () => {
    trackEvent(ORDER_SEND_FAIL, { ...stockProps, ...formProps });
  };

  const eventViewInsufficientBuy = () => {
    trackEvent(VIEW_INSUFFICIENT_ERROR, {
      ...stockProps,
      ...formProps,
      insufficiency: 'Buying Power',
    });
  };

  const eventClickInsufficientBuy = () => {
    trackEvent(CLICK_INSUFFICIENT_CTA, {
      ...stockProps,
      ...formProps,
      insufficiency: 'Buying Power',
    });
  };

  const eventViewInsufficientSell = () => {
    trackEvent(VIEW_INSUFFICIENT_ERROR, { ...stockProps, ...formProps, insufficiency: 'Lot' });
  };

  const eventClickInsufficientSell = () => {
    trackEvent(CLICK_INSUFFICIENT_CTA, { ...stockProps, ...formProps, insufficiency: 'Lot' });
  };

  const eventClickOrderBookPrice = () => {
    trackEvent(CLICK_ORDER_BOOK_PRICE, { ...stockProps, ...formProps });
  };

  return {
    eventViewTradePanel,
    eventClickSearchDiscovery,
    eventClickSwitchOrderSide,
    eventClickSwitchOrderType,
    eventClickSubmitBuy,
    eventClickConfirmBuy,
    eventClickSubmitSell,
    eventClickConfirmSell,
    eventOrderSendSuccess,
    eventOrderSendFail,
    eventViewInsufficientBuy,
    eventClickInsufficientBuy,
    eventViewInsufficientSell,
    eventClickInsufficientSell,
    eventClickOrderBookPrice,
  };
}

export default useEventTracker;
